import { Box, Chip, FormControlLabel, Radio, RadioGroup, TextField, Typography, Tooltip, Alert, Divider, IconButton } from "@mui/material";
import MediaPicker from "../MediaPicker";
import type { Placement, PlatformConfiguration } from "../models";
import { useEffect, useState } from "react";
import { Info, AutoAwesome, LocationOn, PersonAddAlt } from "@mui/icons-material";

type Props = {
  caption: string;
  onCaptionChange: (value: string) => void;
  media: string[];
  onMediaChange: (urls: string[]) => void;
  config: Extract<PlatformConfiguration, { platform: "facebook" }>;
  onConfigChange: (cfg: Extract<PlatformConfiguration, { platform: "facebook" }>) => void;
  maxLength: number;
  onMediaKindsChange: (info: { hasPhoto: boolean; hasVideo: boolean }) => void;
  hasPhoto: boolean;
  hasVideo: boolean;
  onAiClick?: () => void;
};

enum KeyCode {
  Enter = "Enter",
  Backspace = "Backspace",
  Comma = ","
}

export default function FacebookTab({
  caption,
  onCaptionChange,
  media,
  onMediaChange,
  config,
  onConfigChange,
  maxLength,
  onMediaKindsChange,
  hasPhoto,
  hasVideo,
  onAiClick
}: Props) {
  const handlePlacement = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    onConfigChange({ ...config, placement: value as Placement });
  };

  const multiMedia = media.length >= 2;
  const onlyPhotos = media.length > 0 && !hasVideo;
  const mixedKinds = hasPhoto && hasVideo;

  const reelsDisabled = multiMedia || onlyPhotos;
  const reelsTooltip = multiMedia
    ? "Cannot be used for posts with multiple media"
    : onlyPhotos
      ? "Cannot post photo to reels"
      : "";

  const storiesDisabled = multiMedia;
  const storiesTooltip = storiesDisabled ? "Cannot be used for posts with multiple media" : "";

  useEffect(() => {
    if ((config.placement === "reels" && reelsDisabled) || (config.placement === "stories" && storiesDisabled)) {
      onConfigChange({ ...config, placement: "timeline" });
    }
  }, [reelsDisabled, storiesDisabled, media.join("|")]);

  const [collabInput, setCollabInput] = useState("");
  const collabArray = (config.collaborators || "").split(",").map(s => s.trim()).filter(Boolean);

  const handleAddCollaborator = () => {
    const trimmed = collabInput.trim().replace(/^@/, "");
    if (trimmed && !collabArray.includes(trimmed)) {
      onConfigChange({ ...config, collaborators: [...collabArray, trimmed].join(", ") });
    }
    setCollabInput("");
  };

  const handleRemoveCollaborator = (index: number) => {
    onConfigChange({ ...config, collaborators: collabArray.filter((_, i) => i !== index).join(", ") });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === KeyCode.Enter || e.key === KeyCode.Comma) {
      e.preventDefault();
      handleAddCollaborator();
    }
    if (e.key === KeyCode.Backspace && !collabInput && collabArray.length) {
      handleRemoveCollaborator(collabArray.length - 1);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.box}>
        <TextField
          multiline
          minRows={5}
          label="Facebook caption"
          value={caption}
          onChange={e => onCaptionChange(e.target.value)}
          fullWidth
          sx={onAiClick ? styles.aiTextField : undefined}
        />
        {onAiClick && (
          <Box sx={styles.aiButton}>
            <Tooltip title="Generate caption with AI">
              <IconButton size="small" color="secondary" onClick={onAiClick}>
                <AutoAwesome fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Box sx={styles.counter}>
          <Typography variant="caption" color={caption.length > maxLength ? "error.main" : "text.secondary"}>
            {caption.length}/{maxLength}
          </Typography>
        </Box>
      </Box>

      {(config.placement === "reels" || config.placement === "stories") && media.length === 0 && (
        <Alert variant="filled" severity="error">
          {config.placement === "reels"
            ? "At least one media is required to post to Facebook Reels."
            : "At least one media is required to post to Facebook Stories."}
        </Alert>
      )}

      {config.placement === "timeline" && mixedKinds && (
        <Alert variant="filled" severity="error">Cannot upload photo and video in one post for Timeline.</Alert>
      )}

      <Box sx={styles.mediaHeaderBox}>
        <Typography sx={styles.sectionTitle} variant="subtitle2">Add Media</Typography>
        <Tooltip title="You can upload up to 10 media, 2 GB each" color="text.secondary">
          <Info fontSize="small" color="action" />
        </Tooltip>
      </Box>

      <MediaPicker
        value={media}
        onChange={onMediaChange}
        maxItems={10}
        onKindsChange={onMediaKindsChange}
      />

      <Box sx={styles.configBox}>
        <Divider />
        <Typography variant="subtitle2" sx={styles.sectionTitle}>Platform Configurations</Typography>        <RadioGroup row value={config.placement} onChange={handlePlacement}>
          <FormControlLabel value="timeline" control={<Radio />} label="Timeline" />

          <Tooltip title={reelsDisabled ? reelsTooltip : ""} disableHoverListener={!reelsDisabled}>
            <span>
              <FormControlLabel value="reels" control={<Radio disabled={reelsDisabled} />} label="Reels" />
            </span>
          </Tooltip>

          <Tooltip title={storiesDisabled ? storiesTooltip : ""} disableHoverListener={!storiesDisabled}>
            <span>
              <FormControlLabel value="stories" control={<Radio disabled={storiesDisabled} />} label="Stories" />
            </span>
          </Tooltip>
        </RadioGroup>
      </Box>

      <Box sx={styles.infoBox}>
        <LocationOn sx={styles.icon} />
        <Typography variant="subtitle2" sx={styles.infoText}>Want to add Location and Music?</Typography>
        <Tooltip title="After posting from ARELIA, go to the native app of the social platform and edit the post to include location and the music of your choice.">
          <Info fontSize="small" color="action" />
        </Tooltip>
      </Box>

      <Box>
        <Box sx={styles.infoBoxWithMargin}>
          <PersonAddAlt sx={styles.icon} />
          <Typography variant="subtitle2" sx={styles.infoText}>Collaborators</Typography>
          <Tooltip title="Enter Facebook Page IDs to invite as collaborators on a Video Reel. Type an ID and press Enter to add.">
            <Info fontSize="small" color="action" />
          </Tooltip>
        </Box>
        {collabArray.length > 0 && (
          <Box sx={styles.chipsBox}>
            {collabArray.map((c, i) => (
              <Chip key={i} label={c} onDelete={() => handleRemoveCollaborator(i)} size="small" variant="outlined" />
            ))}
          </Box>
        )}
        <TextField
          size="small"
          placeholder="Type a Page ID and press Enter"
          value={collabInput}
          onChange={(e) => setCollabInput(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
        />
      </Box>
    </Box>
  );
}

const styles = {
  container: { 
    display: "flex", 
    flexDirection: "column", 
    gap: 2 
  },
  box: { 
    position: "relative" 
  },
  aiTextField: { 
    '& .MuiInputBase-input': { pb: '32px' } 
  },
  aiButton: { 
    position: "absolute", 
    bottom: 6, left: 8 
  },
  counter: { 
    position: "absolute", 
    bottom: 6, right: 8 
  },
  mediaHeaderBox: { 
    display: "flex", 
    alignItems: "center", 
    fontSize: 16, 
    gap: 1 
  },
  sectionTitle: { 
    fontSize: 16, 
    fontWeight: 'bold' 
  },
  configBox: { 
    display: "flex", 
    flexDirection: "column", 
    gap: 1 
  },
  infoBox: { 
    display: "flex", alignItems: "center", gap: 1 
  },
  infoBoxWithMargin: { 
    display: "flex", alignItems: "center", gap: 1, mb: 1 
  },
  icon: { 
    fontSize: 20, color: "text.secondary" 
  },
  infoText: { 
    fontSize: 14, 
    fontWeight: 600 
  },
  chipsBox: { 
    display: "flex", 
    flexWrap: "wrap", 
    gap: 0.5, 
    mb: 1 
  }
};
